import { useState, useEffect, useCallback } from "react";
import { whenNoDialog } from "@/lib/modalGate";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { Illustration, type IllustrationName } from "@/components/Illustration";

type Mood = "happy" | "proud" | "sad" | "encouraging" | "celebrating" | "sleeping" | "waving";

interface BuddyState {
  mood: Mood;
  message: string;
  action?: { label: string; path: string };
}

function getTimeSinceActive(lastActiveAt: string | null): number {
  if (!lastActiveAt) return Infinity;
  return (Date.now() - new Date(lastActiveAt).getTime()) / (1000 * 60 * 60);
}

function determineBuddyState(
  stats: any,
  weeklyStats: any,
  user: any,
  location: string
): BuddyState {
  const hoursSinceActive = getTimeSinceActive(user?.lastActiveAt);

  if (hoursSinceActive > 72) {
    return {
      mood: "sad",
      message: "I missed you! It's been a few days. Ready for a quick practice session?",
      action: { label: "Let's Go", path: "/learning" },
    };
  }

  if (hoursSinceActive > 24) {
    return {
      mood: "sleeping",
      message: "Hey there! Haven't seen you since yesterday. A little practice each day goes a long way!",
      action: { label: "Start Practicing", path: "/learning" },
    };
  }

  if (stats?.accuracy >= 80 && stats?.totalQuestions >= 20) {
    return {
      mood: "celebrating",
      // Named the exam, and StudyBuddy is shown on every page for every grade, so a CSEC student
      // was being told PEP would be a breeze. The encouragement works without naming an exam.
      message: `Amazing work! ${stats.accuracy}% accuracy - you're on fire! Keep this up and the exam will look a lot less scary.`,
    };
  }

  if (stats?.accuracy >= 60 && stats?.totalQuestions >= 10) {
    return {
      mood: "proud",
      message: "You're doing really well! Try challenging yourself with a harder topic next.",
      action: { label: "Try New Topic", path: "/learning" },
    };
  }

  if (weeklyStats?.currentStreak >= 3) {
    return {
      mood: "happy",
      message: `${weeklyStats.currentStreak}-day streak! You're building great study habits. Keep it going!`,
    };
  }

  if (stats?.totalQuestions === 0) {
    return {
      mood: "waving",
      message: "Welcome! Tap Subjects to start answering questions and leveling up!",
      action: { label: "Start Learning", path: "/learning" },
    };
  }

  if (stats?.accuracy < 50 && stats?.totalQuestions >= 5) {
    return {
      mood: "encouraging",
      message: "Don't worry, practice makes perfect! Try reviewing topics you've already started.",
      action: { label: "Review Topics", path: "/learning" },
    };
  }

  if (location === "/dashboard") {
    const tips = [
      { mood: "happy" as Mood, message: "Tip: Try answering at least 10 questions a day to level up faster!" },
      { mood: "waving" as Mood, message: "Did you know? You can challenge your friends from the leaderboard!", action: { label: "Leaderboard", path: "/leaderboard" } },
      { mood: "happy" as Mood, message: "Tip: Focus on one subject at a time for better results!" },
      { mood: "encouraging" as Mood, message: "Check your progress report to see how you're improving!", action: { label: "View Report", path: "/progress-report" } },
    ];
    return tips[Math.floor(Math.random() * tips.length)];
  }

  return {
    mood: "happy",
    message: "You're doing great! Every question you answer brings you closer to your goal!",
  };
}

/** One illustration voice: Owlbert, picked to match the buddy's mood. */
const MOOD_ILLUSTRATION: Record<Mood, IllustrationName> = {
  happy: "owl-wave",
  proud: "owl-cheer",
  sad: "owl-sleepy",
  encouraging: "owl-wave",
  celebrating: "owl-cheer",
  sleeping: "owl-sleepy",
  waving: "owl-wave",
};

const STORAGE_KEY = "edufocal_buddy_last_shown";
const SHOW_INTERVAL_MS = 2 * 60 * 60 * 1000;
const AUTO_HIDE_MS = 12000;

export function StudyBuddy() {
  const { user, isAuthenticated } = useAuth();
  const [location, navigate] = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [buddyState, setBuddyState] = useState<BuddyState | null>(null);
  const [side, setSide] = useState<"left" | "right">("right");

  const { data: dashboardStats } = useQuery({
    queryKey: ["/api/dashboard/stats"],
    enabled: isAuthenticated && !!user,
    staleTime: 60000,
  });

  const { data: weeklyStats } = useQuery({
    queryKey: ["/api/dashboard/weekly-stats"],
    enabled: isAuthenticated && !!user,
    staleTime: 60000,
  });

  const shouldShow = useCallback(() => {
    const lastShown = localStorage.getItem(STORAGE_KEY);
    if (!lastShown) return true;
    return Date.now() - parseInt(lastShown, 10) > SHOW_INTERVAL_MS;
  }, []);

  useEffect(() => {
    setVisible(false);
  }, [location]);

  useEffect(() => {
    if (!isAuthenticated || !user || dismissed) return;

    // The bottom-right corner is the support button's; the buddy keeps to the left so they never overlap.
    setSide("left");

    let cancel: (() => void) | null = null;
    const timer = setTimeout(() => {
      if (!shouldShow()) return;
      // Wait until no popup (grade check, announcement, install prompt) is on screen.
      cancel = whenNoDialog(() => {
        setBuddyState(determineBuddyState(dashboardStats, weeklyStats, user, location));
        setVisible(true);
        localStorage.setItem(STORAGE_KEY, Date.now().toString());
      }, { everyMs: 4000, maxWaitMs: 120000 });
    }, 4000);

    return () => { clearTimeout(timer); cancel?.(); };
  }, [isAuthenticated, user, location, dashboardStats, weeklyStats, dismissed, shouldShow]);

  useEffect(() => {
    if (!visible) return;
    const hideTimer = setTimeout(() => {
      setVisible(false);
    }, AUTO_HIDE_MS);
    return () => clearTimeout(hideTimer);
  }, [visible]);

  if (!isAuthenticated || !user || !buddyState) return null;
  if (location.startsWith("/ref/") || location.startsWith("/parent/")) return null;

  const positionClass = side === "right" ? "right-3" : "left-3";

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className={`fixed ${positionClass} z-40`}
          style={{ bottom: "calc(4.5rem + env(safe-area-inset-bottom, 0px))" }}
          initial={{ y: 80, opacity: 0, scale: 0.8 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 40, opacity: 0, scale: 0.8 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
        >
          <div className="flex items-end gap-2" style={{ flexDirection: side === "right" ? "row" : "row-reverse" }}>
            <div className="bg-card border rounded-lg shadow-sm p-3 max-w-[220px] relative">
              <Button
                size="icon"
                variant="ghost"
                className="absolute -top-2 -right-2 rounded-full"
                onClick={() => {
                  setVisible(false);
                  setDismissed(true);
                }}
              >
                <X className="h-3 w-3" />
              </Button>
              <p className="text-xs text-foreground leading-relaxed pr-3">{buddyState.message}</p>
              {buddyState.action && (
                <Button
                  size="sm"
                  className="mt-2 w-full rounded-lg text-xs"
                  onClick={() => {
                    navigate(buddyState.action!.path);
                    setVisible(false);
                  }}
                >
                  {buddyState.action.label}
                </Button>
              )}
            </div>
            <motion.div
              className="flex-shrink-0 cursor-pointer"
              whileTap={{ scale: 0.9 }}
              animate={buddyState.mood === "celebrating" ? { y: [0, -4, 0] } : buddyState.mood === "sad" ? { y: [0, 2, 0] } : {}}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              onClick={() => {
                setVisible(false);
                setDismissed(true);
              }}
            >
              <Illustration name={MOOD_ILLUSTRATION[buddyState.mood]} size={56} />
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
